// What may change in a quote (Diagnostic Intake) once it means something outside
// Oike. One rule for the server (airtable-proxy, diagnostic-admin, intake-review)
// and the editor, which only reflects it.
// - Accepted (Quote Status = Won): its lines, currency, conditions and market are
//   locked. Preparing the start of a service identifies a line by its position
//   (src/serviceStart.js › lineKeyOf), so editing or reordering the lines of an
//   accepted quote would change what a linked process points to. The acceptance
//   itself cannot be undone from the generic path either, and it is registered only
//   by quote-accept.mts (Ale) or from the person's space (never Quote Status = Won
//   through the generic path), which freeze the definitive document.
// - Sent but not accepted (Consulting Landing): still the same quote, edited on the
//   same record; publishing again updates the same document and link
//   (publish-proposal.mts) and keeps the date and total of every send. A published
//   document is never deleted nor unlinked.
// - An earlier version (Revised By: a later version points to it): history, read only.
//   No new revisions are created any more (docs/servicios/cotizaciones.md).
// - History: a linked document is never relinked or unlinked, a payment date is
//   never cleared and an acceptance date is never cleared while accepted.
// - Delete: never a quote that was accepted, published, paid or linked to a
//   service (Diagnostics › Origin Quotes, seen here as its reciprocal link).
// - Corrected acceptance (quote-correction.mts: registered by mistake, or cancelled
//   by the client): the content stays locked as if still accepted, the correction
//   mark in Pipeline Facts Note is never removed and Won At is never cleared from
//   the generic path. Payments and linked services are never touched.
// - Revision Of (a link to the previous version; Airtable allows several): a write sets at
//   most one link, and an existing link never changes. A record with more than one is an
//   anomaly to review (revisionAnomaly): nothing picks one of them.
import { ACCEPTANCE_ERROR_MARK, ACCEPTANCE_CANCEL_MARK, acceptanceMarksOf } from './pipelineFacts.js';

export const QUOTE_CONTENT_FIELDS = ['Quote Lines JSON', 'Quote Currency', 'Quote Conditions', 'Quote Market'];

const name = (v) => (v && typeof v === 'object' && !Array.isArray(v) ? v.name : v) || '';
const links = (v) => (Array.isArray(v) ? v.map(x => (typeof x === 'object' ? x?.id : x)).filter(Boolean) : []);
const empty = (v) => v === null || v === undefined || v === '' || (Array.isArray(v) && !v.length);
const same = (a, b) => JSON.stringify(Array.isArray(a) ? links(a) : a ?? '') === JSON.stringify(Array.isArray(b) ? links(b) : b ?? '');

export const isAccepted = (f = {}) => name(f['Quote Status']) === 'Won';
export const isPublished = (f = {}) => links(f['Consulting Landing']).length > 0;
/** It was accepted at some point: accepted now, an acceptance date, or a corrected acceptance. */
export const wasAccepted = (f = {}) => {
  const m = acceptanceMarksOf(f['Pipeline Facts Note']);
  return isAccepted(f) || !empty(f['Won At']) || m.error || m.cancelled;
};
/** An earlier version of a chain: a later version points to it (history, read only). */
export const isReplaced = (f = {}) => links(f['Revised By']).length > 0;
/** The lines, currency, conditions and market can no longer be edited: only an acceptance (or an earlier version) locks them. */
export const contentLocked = (f = {}) => wasAccepted(f) || isReplaced(f);
/** A recalculation or a draft save may replace the content (a draft that nothing outside Oike depends on). */
export const draftEditable = (f = {}) => !contentLocked(f) && ['', 'Draft'].includes(name(f['Quote Status']));
// Diagnostics 2: the reciprocal of Diagnostics › Origin Quotes (docs/diagnostic/airtable-schema.md)
const linkedToService = (f = {}) => links(f['Diagnostics 2']).length > 0;

/** The previous version(s) a record links to. */
export const revisionLinksOf = (f = {}) => links(f['Revision Of']);
/** More than one previous version: to review, never resolved by the code. */
export const revisionAnomaly = (f = {}) => revisionLinksOf(f).length > 1;

/** Creating a quote: null when allowed. */
export function quoteCreateProblem(fields = {}) {
  // «Solo administración» changes only by its explicit action (quote-recipient.mts), never through the generic path
  if (Object.prototype.hasOwnProperty.call(fields, 'Admin Only')) return { status: 409, error: 'admin_only_path' };
  if (name(fields['Quote Status']) === 'Won' || !empty(fields['Won At'])) return { status: 409, error: 'quote_accept_path' };
  // No new revisions: a quote is edited on its own record until accepted
  if (Object.prototype.hasOwnProperty.call(fields, 'Revision Of') && links(fields['Revision Of']).length) return { status: 409, error: 'revisions_closed' };
  return null;
}

/** A write to an existing quote: null when allowed, or { status, error } (a code the screens translate). */
export function quotePatchProblem(existing = {}, patch = {}) {
  const has = (k) => Object.prototype.hasOwnProperty.call(patch, k);
  if (has('Admin Only')) return { status: 409, error: 'admin_only_path' };
  if (has('Revision Of')) {
    if (links(patch['Revision Of']).length > 1) return { status: 409, error: 'quote_revision_single' };
    if (revisionLinksOf(existing).length && !same(patch['Revision Of'], existing['Revision Of'])) return { status: 409, error: 'quote_revision_fixed' };
    if (!revisionLinksOf(existing).length && links(patch['Revision Of']).length) return { status: 409, error: 'revisions_closed' };
  }
  if (contentLocked(existing) && QUOTE_CONTENT_FIELDS.some(k => has(k) && !same(patch[k], existing[k]))) {
    return { status: 409, error: isAccepted(existing) ? 'quote_accepted' : wasAccepted(existing) ? 'quote_was_accepted' : 'quote_replaced' };
  }
  // An acceptance is registered only by its own path (quote-accept.mts, the person's space), which freezes the document
  if (!isAccepted(existing) && has('Quote Status') && name(patch['Quote Status']) === 'Won') return { status: 409, error: 'quote_accept_path' };
  if (!wasAccepted(existing) && has('Won At') && !empty(patch['Won At'])) return { status: 409, error: 'quote_accept_path' };
  if (!isAccepted(existing) && (has('Accepted Via') || (has('Accepted By') && !empty(patch['Accepted By'])))) return { status: 409, error: 'quote_accept_path' };
  // The trace of a corrected acceptance stays in the note
  if (has('Pipeline Facts Note')) {
    const note = String(patch['Pipeline Facts Note'] || '');
    const before = acceptanceMarksOf(existing['Pipeline Facts Note']);
    if ((before.error && !note.includes(ACCEPTANCE_ERROR_MARK)) || (before.cancelled && !note.includes(ACCEPTANCE_CANCEL_MARK))) return { status: 409, error: 'quote_correction' };
  }
  if (isAccepted(existing) && has('Quote Status') && name(patch['Quote Status']) !== 'Won') return { status: 409, error: 'quote_accepted' };
  if (isPublished(existing) && has('Consulting Landing') && !same(patch['Consulting Landing'], existing['Consulting Landing'])) return { status: 409, error: 'quote_document' };
  if (!empty(existing['Payment Received At']) && has('Payment Received At') && empty(patch['Payment Received At'])) return { status: 409, error: 'quote_payment' };
  // An acceptance date is history: never cleared here (a mistaken acceptance is corrected by quote-correction.mts)
  if (!empty(existing['Won At']) && has('Won At') && empty(patch['Won At'])) return { status: 409, error: isAccepted(existing) ? 'quote_accepted' : 'quote_was_accepted' };
  return null;
}

/** Deleting a quote: null when allowed. */
export function quoteDeleteProblem(existing = {}) {
  if (isAccepted(existing)) return { status: 409, error: 'quote_accepted' };
  if (wasAccepted(existing)) return { status: 409, error: 'quote_was_accepted' };
  if (isPublished(existing)) return { status: 409, error: 'quote_published' };
  if (!empty(existing['Payment Received At']) || ['Partial', 'Received'].includes(name(existing['Payment Status']))) return { status: 409, error: 'quote_payment' };
  if (linkedToService(existing)) return { status: 409, error: 'quote_service' };
  return null;
}

/** Deleting a landing: never a published proposal document. */
export const landingDeleteProblem = (existing = {}) => (!empty(existing['Published At']) ? { status: 409, error: 'quote_document' } : null);

/** What the screens say for each code. */
export const QUOTE_RULE_MESSAGES = {
  quote_accepted: 'La contratación está aceptada: sus líneas, moneda y condiciones no se cambian ni se reordenan, y la aceptación no se deshace aquí.',
  quote_was_accepted: 'Esta cotización fue aceptada y la aceptación se corrigió o el cliente canceló: su contenido queda como estaba y no se borra.',
  quote_correction: 'La línea de corrección de la aceptación queda en la nota de hechos: no se quita.',
  quote_revision_single: 'Una revisión apunta a una sola versión anterior.',
  revisions_closed: 'Ya no se crean revisiones: la cotización se edita en su ficha hasta que el cliente la acepta. Las versiones anteriores quedan como historial.',
  quote_revision_fixed: 'La versión anterior de una revisión no se cambia.',
  quote_has_charges: 'Esta versión tiene cobros: su pago se registra en Cotizaciones y pagos. El estado de pago anterior queda como historial.',
  quote_published: 'La propuesta ya se publicó: su documento no se borra.',
  quote_replaced: 'Esta es una versión anterior: queda como historial y no se cambia. Se trabaja sobre la versión vigente.',
  admin_only_path: '«Solo administración» se cambia solo desde la ficha, con su acción explícita.',
  quote_accept_path: 'La aceptación se registra con «Registrar aceptación» en la ficha (o la confirma el cliente desde su espacio), para congelar el documento aceptado.',
  quote_document: 'El documento emitido de la propuesta no se cambia ni se desvincula.',
  quote_payment: 'Hay un pago registrado en esta cotización: no se borra ni se quita su fecha.',
  quote_service: 'Esta cotización ya tiene un servicio vinculado: no se borra.',
};
